import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Undo2, Redo2, History, Loader2, Check, CloudOff } from 'lucide-react';

/**
 * Shared editor toolbar: Undo / Redo buttons + autosave status chip (+ optional History).
 * Replaces per-editor Save buttons.
 *
 * Props:
 *  - canUndo, canRedo: boolean
 *  - onUndo, onRedo: () => void
 *  - status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
 *  - onOpenHistory?: () => void  (omit to hide History button)
 *  - className?: string
 */
export default function EditorAutosaveBar({ canUndo, canRedo, onUndo, onRedo, status, onOpenHistory, showStatus = true, className = '' }) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e) => {
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      if (e.shiftKey) { if (canRedo) onRedo(); }
      else { if (canUndo) onUndo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canUndo, canRedo, onUndo, onRedo]);

  const statusEl = (() => {
    switch (status) {
      case 'saving':
        return (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('autosave.saving', 'Saving…')}
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-1.5 text-amber-500">
            <CloudOff className="h-3.5 w-3.5" />
            {t('autosave.offline', 'Offline — retrying')}
          </span>
        );
      case 'dirty':
        return (
          <span className="text-muted-foreground">
            {t('autosave.unsaved', 'Unsaved changes')}
          </span>
        );
      case 'idle':
        return null;
      case 'saved':
      default:
        return (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Check className="h-3.5 w-3.5 text-emerald-500" />
            {t('autosave.saved', 'All changes saved')}
          </span>
        );
    }
  })();

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 md:h-9 md:w-9 rounded-full"
        disabled={!canUndo}
        onClick={onUndo}
        tooltip={t('autosave.undo', 'Undo')}
        aria-label={t('autosave.undo', 'Undo')}
        data-testid="editor-undo-btn"
      >
        <Undo2 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 md:h-9 md:w-9 rounded-full"
        disabled={!canRedo}
        onClick={onRedo}
        tooltip={t('autosave.redo', 'Redo')}
        aria-label={t('autosave.redo', 'Redo')}
        data-testid="editor-redo-btn"
      >
        <Redo2 className="h-4 w-4" />
      </Button>
      {onOpenHistory && (
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 md:h-9 md:w-9 rounded-full"
          onClick={onOpenHistory}
          tooltip={t('routines.history.button', 'History')}
          aria-label={t('routines.history.button', 'History')}
          data-testid="editor-history-btn"
        >
          <History className="h-4 w-4" />
        </Button>
      )}
      {showStatus && (
        <span className="text-xs ml-1" data-testid="autosave-status">{statusEl}</span>
      )}
    </div>
  );
}
